import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { useFormikContext } from "formik"
import { getInNested } from "../dynamic-form.helper"
import "./multi-select-dropdown.scss"

// Shared, self-contained multi-select dropdown with a special "All" choice.
//
// Value contract: a string[] of the picked option values. An EMPTY array means "All" (the whole
// set) — there is no sentinel value in the array for All, so consumers read `value.length === 0`
// as "all / whole incident". Checking the "All" row clears every specific pick (→ []); checking a
// specific pick clears "All" implicitly (the array becomes non-empty). This is used for role
// selection in every setup mode (solo flat form + independent-group / collaborative roster cells),
// so it must work both as a Formik field and as a plain controlled value/onChange control.

// Presentational core: controlled by `value` (string[]) + `onChange` (string[]) => void.
export function MultiSelectDropdownControl({
  value,
  onChange,
  options = [],
  allLabel = "All",
  disabled = false,
  className = "",
  id,
  ariaLabel,
  singleSelect = false,
}) {
  const [open, setOpen] = useState(false)
  const [panelPos, setPanelPos] = useState(null)
  const rootRef = useRef(null)
  const panelRef = useRef(null)
  const selected = Array.isArray(value) ? value.filter((v) => v !== "") : []
  const isAll = selected.length === 0

  // The panel is rendered through a PORTAL to <body> (not inline) because roster cells live inside
  // `.ctd-roster-tbl { overflow: hidden }`, which would otherwise CLIP the panel — making the options
  // unreachable in Independent-Group / Collaborative rosters. Position it under the button with fixed
  // coordinates from the button's rect, and re-measure on scroll/resize while open.
  const measure = () => {
    const btn = rootRef.current?.querySelector(".ctd-msdd-btn")
    if (!btn) return
    const r = btn.getBoundingClientRect()
    setPanelPos({ top: r.bottom + 2, left: r.left, minWidth: r.width })
  }
  useLayoutEffect(() => {
    if (open) measure()
  }, [open])
  // Keyboard operability: the panel is portalled to <body>, outside the trigger's tab order, so a
  // keyboard user could never reach it. Move focus to the first checkbox when it opens, and return
  // focus to the trigger when it closes (Escape or selection-complete), so the control is fully
  // usable without a mouse.
  useEffect(() => {
    if (open) {
      panelRef.current?.querySelector("input")?.focus()
      return undefined
    }
    // Closed: restore focus to the trigger only if focus is lost/inside the removed panel.
    const btn = rootRef.current?.querySelector(".ctd-msdd-btn")
    if (btn && (document.activeElement === document.body || !document.activeElement)) btn.focus()
    return undefined
  }, [open])
  useEffect(() => {
    if (!open) return undefined
    const onDocMouseDown = (e) => {
      if (rootRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false)
        rootRef.current?.querySelector(".ctd-msdd-btn")?.focus()
      }
    }
    const onReposition = () => measure()
    document.addEventListener("mousedown", onDocMouseDown)
    document.addEventListener("keydown", onKey)
    // capture:true so a scroll on ANY ancestor (the roster table, a modal body) repositions the panel.
    window.addEventListener("scroll", onReposition, true)
    window.addEventListener("resize", onReposition)
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown)
      document.removeEventListener("keydown", onKey)
      window.removeEventListener("scroll", onReposition, true)
      window.removeEventListener("resize", onReposition)
    }
  }, [open])

  const toggleSpecific = (optionValue) => {
    // singleSelect (radio): choosing a role REPLACES the selection with just that one; to reset,
    // choose the "All roles" option. Multi (default): checkbox toggle.
    if (singleSelect) {
      onChange([optionValue])
      return
    }
    const next = selected.includes(optionValue) ? selected.filter((v) => v !== optionValue) : [...selected, optionValue]
    onChange(next)
  }
  const chooseAll = () => onChange([])

  // Summarize over the SELECTED values (not just known options) so an unknown value — e.g. a stale
  // role from a CSV import — is shown verbatim rather than hidden (which would make a non-empty,
  // wrongly-scoped selection read as the "All" label). Checking "All" clears it.
  const summary = isAll ? allLabel : selected.map((v) => options.find((o) => o.value === v)?.display ?? v).join(", ") || allLabel

  const panel =
    open && panelPos
      ? createPortal(
          <div
            className="ctd-msdd-panel ctd-msdd-panel-portal"
            ref={panelRef}
            role="group"
            aria-label={`${allLabel} — ${singleSelect ? "select one" : "select one or more"}`}
            style={{ position: "fixed", top: panelPos.top, left: panelPos.left, minWidth: panelPos.minWidth }}
          >
            <label className="ctd-msdd-opt">
              <input type={singleSelect ? "radio" : "checkbox"} checked={isAll} onChange={chooseAll} />
              <span>{allLabel}</span>
            </label>
            <div className="ctd-msdd-divider" />
            {(options ?? []).map((option, idx) => (
              <label className="ctd-msdd-opt" key={`${option.value}-${idx}`}>
                <input
                  type={singleSelect ? "radio" : "checkbox"}
                  checked={selected.includes(option.value)}
                  onChange={() => toggleSpecific(option.value)}
                />
                <span>{option.display ?? option.value}</span>
              </label>
            ))}
          </div>,
          document.body
        )
      : null

  return (
    <div className={`ctd-msdd ${className}`} ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        id={id}
        className={`ctd-msdd-btn form-select${disabled ? " ctd-msdd-disabled" : ""}`}
        disabled={disabled}
        // No aria-haspopup: "true" means MENU in ARIA and "listbox" promises option semantics —
        // the popup is a labeled CHECKBOX GROUP (role="group"), so aria-expanded alone describes it.
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel} — ${summary}` : undefined}
        onClick={() => !disabled && setOpen((o) => !o)}
        title={summary}
      >
        <span className="ctd-msdd-summary">{summary}</span>
      </button>
      {panel}
    </div>
  )
}

// Formik-bound wrapper used by the flat dynamic form (dynamic-form.helper render dispatch).
function InputMultiSelect(props) {
  const { id, label, name, optionData, errorMessage, fieldData, isNestedFieldName, isRequired } = props
  const { values, touched, errors, setFieldValue } = useFormikContext()
  const normalizedFieldName = isNestedFieldName ? name : `['${name}']`
  const current = getInNested(values, name, isNestedFieldName)
  return (
    <Fragment>
      <div className="custom-form-block">
        <label htmlFor={id} className="form-label mt-2">
          {label} {isRequired && <sup>*</sup>}
        </label>
        <MultiSelectDropdownControl
          id={id}
          ariaLabel={label}
          // A LEGACY saved/duplicated value can be a scalar string — preserve it as a one-item
          // selection (matching the roster-cell coercion) rather than displaying/overwriting it as All.
          value={Array.isArray(current) ? current : current ? [current] : []}
          onChange={(next) => setFieldValue(normalizedFieldName, next)}
          options={optionData ?? []}
          allLabel={fieldData?.allLabel ?? "All"}
          singleSelect={fieldData?.singleSelect === true}
          disabled={fieldData?.enabled === false}
        />
        {fieldData?.helper ? <div className="field-helper">{fieldData.helper}</div> : null}
        <div className="invalid-feedback d-block">
          {getInNested(touched, name, isNestedFieldName) && getInNested(errors, name, isNestedFieldName) ? errorMessage : ""}
        </div>
      </div>
    </Fragment>
  )
}

InputMultiSelect.propTypes = {}

export default InputMultiSelect
